import React from 'react';
import ReactSelect from 'react-select';

import { INPUT, LABEL, SELECT_DARK } from '../referral/brandTheme';
import { BRANCH_OPTIONS, OTHER, rankOptionsForBranch } from './rankOptions';

// "Are you a veteran?" + branch + rank, shared by the website form
// (/reach-out) and the event / partner QR page (2026-10-14) so both ask the
// same thing the same way. The parent keeps the answers in `value` and
// stores what militaryAnswers() returns.

export const EMPTY_MILITARY = {
  isVeteran: '', // 'yes' | 'no'
  branchOption: null, // react-select option
  rankOption: null, // react-select option (scoped to the branch)
  rankOther: '',
};

/** { isVeteran, rank, complete } from the form state. */
export const militaryAnswers = (value) => {
  const branch = value.branchOption?.value || '';
  const isOtherBranch = branch === OTHER;
  const isOtherRank = isOtherBranch || value.rankOption?.value === OTHER;
  const typedRank = value.rankOther.trim();
  // Picked ranks already carry the branch ("Army · E-5 Sergeant"); typed
  // ones get it prefixed when a real branch was chosen.
  const rank = isOtherRank
    ? typedRank && !isOtherBranch
      ? `${branch} · ${typedRank}`
      : typedRank
    : value.rankOption?.value || '';
  return {
    isVeteran: value.isVeteran,
    rank,
    complete: !!(value.isVeteran && branch && rank),
  };
};

const MilitaryQuestions = ({ value, onChange }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const branch = value.branchOption?.value || '';
  const isOtherBranch = branch === OTHER;

  return (
    <>
      <div>
        <div className={LABEL}>
          Are you a veteran?
          <span className="text-rose-400"> *</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {['yes', 'no'].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => set({ isVeteran: v })}
              className={`h-10 rounded-md text-sm font-medium transition-colors ${
                value.isVeteran === v
                  ? 'bg-white text-black'
                  : 'bg-black text-neutral-400 hover:text-white'
              }`}
            >
              {v === 'yes' ? 'Yes' : 'No'}
            </button>
          ))}
        </div>
      </div>

      {/* Branch + rank always show: these pages are for military folks, and
          active-duty members answer "No" to the veteran question while still
          having a branch and rank. */}
      <div className="space-y-3">
        <div>
          <div className={LABEL}>
            Which branch of the military?
            <span className="text-rose-400"> *</span>
          </div>
          <ReactSelect
            styles={SELECT_DARK}
            options={BRANCH_OPTIONS}
            value={value.branchOption}
            // A branch change invalidates the picked rank.
            onChange={(opt) => set({ branchOption: opt, rankOption: null })}
            placeholder="Select your branch…"
            isSearchable={false}
          />
        </div>
        {branch && !isOtherBranch ? (
          <div>
            <div className={LABEL}>
              What is your rank?
              <span className="text-rose-400"> *</span>
            </div>
            <ReactSelect
              styles={SELECT_DARK}
              options={rankOptionsForBranch(branch)}
              value={value.rankOption}
              onChange={(opt) => set({ rankOption: opt })}
              placeholder="Select your rank…"
              isSearchable
            />
          </div>
        ) : null}
        {isOtherBranch || value.rankOption?.value === OTHER ? (
          <div>
            <div className={LABEL}>
              {isOtherBranch ? 'What is your rank?' : 'Enter your rank'}
              <span className="text-rose-400"> *</span>
            </div>
            <input
              className={INPUT}
              value={value.rankOther}
              onChange={(e) => set({ rankOther: e.target.value })}
              placeholder="E-6, O-4"
              maxLength={40}
            />
          </div>
        ) : null}
      </div>
    </>
  );
};

export default MilitaryQuestions;
